import React, { useCallback } from 'react';
import { usePricingFormContext } from './PricingFormContext';
import {Col, Row, Layout, Divider, InputNumber, Select, Button, message} from 'antd';

// 检查数值的小数位数是否超过2位
const checkDecimalPlaces = (value: number | null | undefined, fieldName: string): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex === -1) {
        return true; // 没有小数部分，通过
    }
    const decimalPart = str.substring(decimalIndex + 1);
    if (decimalPart.length > 2) {
        message.warning(`${fieldName}最多支持两位小数，当前输入超过限制`);
        return false;
    }
    return true;
};

const headerStyle: React.CSSProperties = {
    color: '#090909ff',
    lineHeight: '25px',
    backgroundColor: '#fff',
    paddingLeft: '0px',
    paddingRight: '0px',
};
const dividerStyle: React.CSSProperties = {
    margin: '15px',
};

export interface WeightOrVolumeWeightCard {
    weightOrVolumeWeight?: number | null;
    weightOrVolumeWeightUnit?: string;
    priceUsd?: number | null;
}

export interface WeightOrVolumeWeightData {
    cards: WeightOrVolumeWeightCard[];
}

const PriceCard: React.FC<{
    cardIndex: number;
    card: WeightOrVolumeWeightCard;
    onUpdate: (index: number, card: WeightOrVolumeWeightCard) => void;
    onDelete: () => void;
    onCopy: () => void;
    disabled?: boolean;
    showActions?: boolean; // 是否显示操作按钮
}> = ({ cardIndex, card, onUpdate, onDelete, onCopy, disabled = false, showActions = true }) => {
    const { Header, Content } = Layout;
    
    const units = [
        {value: 'kg', label: 'kg/千克'},
        {value: 'lb', label: 'lb/英磅'},
    ];

    const handleUpdate = (field: keyof WeightOrVolumeWeightCard, value: any) => {
        onUpdate(cardIndex, {...card, [field]: value});
    };

    return (
        <Layout style={{backgroundColor:'#fff', padding:15, border:'1px solid #e8e8e8', borderRadius:5}}>
            <Header style={headerStyle}>
                <Row justify="center" align='middle'>
                    <Col span={18}>
                        <span>Weight/Volume Weight</span>
                    </Col>
                    <Col span={6}>
                        <span>Price/价格</span>
                    </Col>
                </Row>
                <Divider style={dividerStyle} />
            </Header>
            <Layout>
                <Content style={{ background: '#fff'}}>
                    <Row justify="start" gutter={[16, 0]} align="middle">
                        <Col>
                            <span>任意一个大于</span>
                        </Col>
                        <Col>
                            <InputNumber
                                size="small"
                                style={{width: 120}}
                                value={card.weightOrVolumeWeight ?? undefined}
                                disabled={disabled}
                                onChange={(v) => {
                                    if (v !== null && v !== undefined) {
                                        checkDecimalPlaces(v, '重量');
                                    }
                                    handleUpdate('weightOrVolumeWeight', v ?? null);
                                }}
                                placeholder="0"
                            />
                        </Col>
                        <Col>
                            <span>单位</span>
                        </Col>
                        <Col>
                            <Select
                                style={{ width: 120}}
                                options={units}
                                size="small"
                                value={card.weightOrVolumeWeightUnit}
                                disabled={disabled}
                                onChange={(v) => handleUpdate('weightOrVolumeWeightUnit', v)}
                                placeholder="选择单位"
                            />
                        </Col>
                        <Col span={6} style={{textAlign: 'right'}}>
                            <InputNumber
                                size="small"
                                style={{width: 70}}
                                value={card.priceUsd ?? undefined}
                                disabled={disabled}
                                onChange={(v) => {
                                    if (v !== null && v !== undefined) {
                                        checkDecimalPlaces(v, '金额');
                                    }
                                    handleUpdate('priceUsd', v ?? null);
                                }}
                            />
                            {' '}美金
                        </Col>
                    </Row>
                </Content>
            </Layout>
            {showActions && (
                <Row justify="end" style={{marginTop:10}}>
                    <Col>
                        <Button type="default" size="small" onClick={onCopy} disabled={disabled} style={{marginRight: 8}}>复制</Button>
                        <Button type="primary" danger size="small" onClick={onDelete} disabled={disabled}>删除</Button>
                    </Col>
                </Row>
            )}
        </Layout>
    );
};

const WeightOrVolumeWeightTabb: React.FC<{
    tabId: string;
    data?: WeightOrVolumeWeightData;
    onChange?: (data: WeightOrVolumeWeightData) => void;
    feeItemCodeNum?: number;
    onDataChange?: (code: number, data: WeightOrVolumeWeightData) => void;
    disabled?: boolean;
}> = ({ tabId, data: dataProp, onChange, feeItemCodeNum, onDataChange, disabled = false }) => {
    const ctx = usePricingFormContext();
    const data = dataProp ?? (feeItemCodeNum != null ? ctx?.feeItemDataMap?.get(feeItemCodeNum) : undefined);
    const defaultCard = {weightOrVolumeWeight: null, weightOrVolumeWeightUnit: 'lb', priceUsd: null};
    const cards = data?.cards || [defaultCard];
    const card = cards[0] || defaultCard;
    const notify = React.useCallback((payload: WeightOrVolumeWeightData) => {
        if (onDataChange != null && feeItemCodeNum != null) onDataChange(feeItemCodeNum, payload);
        else onChange?.(payload);
    }, [onDataChange, onChange, feeItemCodeNum]);

    const handleUpdateCard = (index: number, updatedCard: WeightOrVolumeWeightCard) => {
        notify({cards: [updatedCard]});
    };

    return (
        <div>
            <PriceCard
                cardIndex={0}
                card={card}
                onUpdate={handleUpdateCard}
                onDelete={() => {}}
                onCopy={() => {}}
                disabled={disabled}
                showActions={false}
            />
        </div>
    );
}

export default React.memo(WeightOrVolumeWeightTabb);

